import React, { createContext, useState, useContext, useEffect, useCallback, useRef, useMemo } from 'react';
    import { supabase } from '@/lib/supabaseClient';
    import { logActivity } from '@/lib/activityLogger';

    const SubcontractorProfileContext = createContext();

    export const useSubcontractorProfile = () => useContext(SubcontractorProfileContext);

    export const SubcontractorProfileProvider = ({ children }) => {
      const [profile, setProfile] = useState(null);
      const [profileLoading, setProfileLoading] = useState(true);
      const [isHydrated, setIsHydrated] = useState(false);
      const [sessionUser, setSessionUser] = useState(null);
      // The issued termination for a terminated crew member (null for everyone
      // else). While it exists the portal is in archive mode: every feature
      // locked, only the archive page live.
      const [termination, setTermination] = useState(null);
      const isFirstLoad = useRef(true);
      // Whether the stored session has been read yet. Until it has, "no
      // session user" means nothing: the first render of the effect below
      // used to call that "hydrated, no profile", and AuthStateListener then
      // bounced every deep link (a reload of Setup › Crew Kit, say) to the
      // login page and on to the dashboard home before the real session had
      // even answered. Found by the render harness (9 Oct 2026).
      const sessionChecked = useRef(false);

      const fetchProfileData = useCallback(async (user) => {
        if (!user) {
          setProfile(null);
          setTermination(null);
          setProfileLoading(false);
          setIsHydrated(true);
          return;
        }

        // Only show full-screen loader on absolute first load to avoid loop
        if (isFirstLoad.current) {
          setProfileLoading(true);
        }
        
        try {
          // select('*') so the optional full_name column is picked up when present
          // without breaking environments where the migration hasn't landed yet.
          const { data: adminRecord, error: adminError } = await supabase
            .from('admins')
            .select('*')
            .eq('email', user.email)
            .maybeSingle();

          if (adminError && adminError.code !== 'PGRST116') console.error('Error checking for admin:', adminError);

          if (adminRecord) {
            // Never fall back to the email address as a display name — the
            // header chip already shows the email, and greetings want a name.
            const adminDisplayName = adminRecord.full_name || user.user_metadata?.full_name || 'Admin';
            
            // Allow Admins to maintain test Subcontractor profiles concurrently
            // handle the case where they might not have a subcontractor record (returns 406 in single())
            const { data: subs, error: profileError } = await supabase
              .from('subcontractors')
              .select(`
                crew_id,
                crews (
                  crew_identifier,
                  depot_id,
                  depots (
                    name,
                    id
                  )
                )
              `)
              .eq('user_id', user.id)
              .limit(1); // Using .limit(1) to avoid 406 console error for admins without profiles

            const dbProfile = subs && subs.length > 0 ? subs[0] : null;

            if (profileError && profileError.code !== 'PGRST116') {
              console.error('Error fetching admin subprofile:', profileError);
            }

            const p = {
              user_id: user.id,
              id: user.id,
              email: user.email,
              role: 'admin',
              is_active: true,
              full_name: adminDisplayName,
              // An admin's `id` is the AUTH user id, which is never a
              // subcontractors.id — they are different keys for every account.
              // Anything addressing crew-owned rows (message_recipients,
              // message_responses, invoices) must use this instead, and must
              // cope with it being null for an admin who has no crew profile.
              subcontractor_id: dbProfile?.id ?? null,
              crew_id: dbProfile?.crew_id,
              crew_identifier: dbProfile?.crews?.crew_identifier,
              depot_name: dbProfile?.crews?.depots?.name,
              depot_id: dbProfile?.crews?.depot_id,
            };
            setProfile(p);
            setTermination(null);
            
            // Log admin login once for the current session
            if (p && !profile && isFirstLoad.current) {
                logActivity('LOGIN', p);
            }
          } else {
            // Read-only accountant? (matched by email, RLS lets them read their own row)
            const { data: accRec } = await supabase
              .from('accountants').select('*').eq('email', user.email).maybeSingle();
            if (accRec && accRec.is_active) {
              const p = {
                user_id: user.id, id: user.id, email: user.email,
                role: 'accountant', is_active: true, full_name: accRec.full_name || 'Accountant',
              };
              setProfile(p);
              setTermination(null);
              if (p && !profile && isFirstLoad.current) logActivity('LOGIN', p);
              return; // skip the subcontractor lookup; finally{} still hydrates
            }

            const { data: dbProfiles, error: dbError } = await supabase
              .from('subcontractors')
              .select(`
                *,
                crews (
                  crew_identifier,
                  depot_id,
                  depots (
                    name,
                    id
                  )
                )
              `)
              .eq('user_id', user.id)
              .limit(1); // Using .limit(1) to avoid 406 console error for users without profiles

            const dbProfile = dbProfiles && dbProfiles.length > 0 ? dbProfiles[0] : null;

            if (dbError && dbError.code !== 'PGRST116') throw dbError;
            
            if (dbProfile) {
              const p = {
                ...dbProfile,
                id: dbProfile.id, // Primary Key for DB lookups
                // Same meaning as on the admin branch: the subcontractors row
                // that owns this person's crew-side records. Here it is simply
                // their own id, so consumers can read one field either way.
                subcontractor_id: dbProfile.id,
                user_id: user.id, // UUID for session lookups
                role: 'subcontractor',
                user_email: user.email,
                crew_identifier: dbProfile.crews?.crew_identifier,
                depot_name: dbProfile.crews?.depots?.name,
                depot_id: dbProfile.crews?.depot_id,
              };
              setProfile(p);

              // Archive mode: a terminated person's portal locks everywhere
              // except the archive. One extra query, and only for them — RLS
              // only ever returns their own issued/expired row.
              if (dbProfile.personnel_status === 'terminated') {
                const { data: term } = await supabase
                  .from('terminations')
                  .select('id, status, issued_at, access_until, letter_subject, letter_html, window_days')
                  .eq('subcontractor_id', dbProfile.id)
                  .in('status', ['issued', 'expired'])
                  .order('issued_at', { ascending: false })
                  .limit(1)
                  .maybeSingle();
                setTermination(term || null);
              } else {
                setTermination(null);
              }
              
              // Log subcontractor login once for the current session
              if (p && !profile && isFirstLoad.current) {
                logActivity('LOGIN', p);
              }
            } else {
              // A partner's Partner Admin (20261300)? Only when the email is not a
              // subcontractor, so no existing login changes. partner_me() matches
              // the signed-in email to an active partnership_users row.
              const { data: partner } = await supabase.rpc('partner_me');
              if (partner?.id) {
                const p = {
                  user_id: user.id, id: user.id, email: user.email, role: 'partner', is_active: true,
                  full_name: partner.full_name || user.email, partnership_id: partner.partnership_id,
                };
                setProfile(p);
                setTermination(null);
                if (!profile && isFirstLoad.current) logActivity('LOGIN', p);
                return; // finally{} still hydrates
              }
              console.warn(`User ${user.id} with email ${user.email} has no matching profile record. Navigation restricted.`);
              setProfile(null);
              setTermination(null);
              // Do NOT sign out automatically here, as it causes race-condition crashes during navigation.
              // Let the ProtectedRoute/AgreementGate handles the redirect to login if session is truly invalid.
            }
          }
        } catch (error) {
          console.error('Error fetching profile:', error);
          setProfile(null);
          setTermination(null);
        } finally {
          isFirstLoad.current = false;
          setProfileLoading(false);
          setIsHydrated(true);
        }
      }, []);

      useEffect(() => {
        const initSession = async () => {
          // Get the initial session immediately
          const { data: { session } } = await supabase.auth.getSession();
          sessionChecked.current = true;
          if (session?.user) {
            setSessionUser(session.user);
          } else {
            setProfile(null);
            setProfileLoading(false);
            setIsHydrated(true);
          }
        };

        initSession();
        
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (_event, session) => {
            sessionChecked.current = true;
            const u = session?.user ?? null;
            setSessionUser(u);
            if (!u) {
              setProfile(null);
              setProfileLoading(false);
              setIsHydrated(true);
            }
          }
        );

        return () => {
          subscription?.unsubscribe();
        };
      }, []);
      
      useEffect(() => {
        // Use user.id as stability key to prevent infinite re-fetching loops
        const userId = sessionUser?.id;
        if (userId) {
          fetchProfileData(sessionUser);
        } else if (sessionChecked.current) {
          // No session user once the session has been read (a sign-out, or
          // the stored session answered empty): not loading, no profile.
          // Before it has been read, this says nothing yet and the gates keep
          // their "verifying" screen up.
          setProfile(null);
          setProfileLoading(false);
          setIsHydrated(true);
        }
      }, [sessionUser?.id, fetchProfileData]);

      const refreshProfile = useCallback(async () => {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (user) {
          await fetchProfileData(user);
        }
      }, [fetchProfileData]);

      const value = useMemo(() => ({
        profile,
        setProfile,
        profileLoading,
        isHydrated,
        refreshProfile,
        user: sessionUser, // the auth user — lets consumers greet by name from user_metadata even before/without a profile row
        termination,
        // Locked = an issued/expired termination exists. The nav and routes
        // read this one flag; nothing else needs to know about terminations.
        archiveLocked: !!termination,
      }), [profile, profileLoading, isHydrated, refreshProfile, sessionUser, termination]);

      return (
        <SubcontractorProfileContext.Provider value={value}>
          {children}
        </SubcontractorProfileContext.Provider>
      );
    };